import { Deserializer } from 'jsonapi-serializer';
import { getFilterClause, createPartialURL } from './utilities/urlBuilder';
import { createPagination } from "./utilities/paginationBuilder";
import { Door, DoorWrapper } from '../models/Door';
import { Name } from "../models/Name"
import { APIConnector } from './api/APIConnector';
import { ListQuery } from '../models/ListQuery';
import { PaginationModel } from '../models/Pagination';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { Filter } from '../models/Filter';
import { genericError } from '../models/GenericError';
import { DeleteEntityResult } from '../models/services/DeleteEntityResult';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';

const apiConnector = new APIConnector();
export const getDoorById = async (id: string): Promise<Door> => {
    try {const locationFields = "fields[location]=terminals,zoneByInterleadingZoneId,locName,zoneByZoneId,locMultipletag,locDesc";
        const terminalFields = "fields[terminal]=devDirection,devFixedAddr,building,floor,devName";
        const buildingFields = 'fields[building]=buildingName';
        const floorFields = 'fields[floor]=floorName';
        const zoneFields = "fields[zone]=zoneName,controller";
        const controllerFields = "fields[controller]=deviceImprox,devName,site";
        const siteFields = "fields[site]=siteName";
        const url = `location/${id}?include=zoneByZoneId.controller.deviceImprox.site,zoneByInterleadingZoneId,terminals.deviceImprox.building,terminals.deviceImprox.floor&${locationFields}&${terminalFields}&${zoneFields}&${controllerFields}&${siteFields}&${buildingFields}&${floorFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error, apiDoor) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });;
                }
                if (apiDoor['id']) {
                    const door = buildDoor(apiDoor);

                    return resolve(door);
                }
                return reject({ status: response.status, message: genericError });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDoors = async (listParameters?: ListQuery): Promise<DoorWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        let locationFields = "fields[location]=terminals,zoneByInterleadingZoneId,locName,";
        locationFields += "zoneByZoneId,locMultipletag,locDesc";
        let includes = "include=zoneByZoneId,terminals,terminals.deviceImprox,";
        includes += "zoneByZoneId.controller,zoneByZoneId.controller.deviceImprox.site";
        const terminalFields = "fields[terminal]=devDirection,devName";
        const zoneFields = "fields[zone]=zoneName,controller";
        const controllerFields = "fields[controller]=deviceImprox,devName,site";
        const siteFields = "fields[site]=siteName";
        const url = `location?${locationFields}&${includes}&${terminalFields}&` +
            `${zoneFields}&${controllerFields}&${siteFields}` +
            createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });;
                }
                
                const doors: Door[] = data.map((door): Door => {
                    return buildDoor(door);
                });

                return resolve({ rows: doors, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDoorsForAZone = async (zoneId: string, listParameters?: ListQuery): Promise<DoorWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = (filter === '' ? `&filter=zoneByZoneId.id==${zoneId}` : `${filter};zoneByZoneId.id==${zoneId}`);
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=zoneByZoneId.controller.deviceImprox.site,terminals.deviceImprox";
        const siteFields = "fields[site]=siteName";
        const controllerFields = "fields[controller]=devName,site";
        const zoneFields = "fields[zone]=zoneName,controller";
        const doorFields = "fields[location]=locName,terminals,zoneByZoneId";
        const terminalFields = "fields[terminal]=devName,devDirection,devFixedAddr";
        const url = `location?${includes}&${siteFields}&${controllerFields}&${zoneFields}&${doorFields}&${terminalFields}${partialUrl}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, doors: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });;
                }

                const zoneDoors: Door[] = doors.map((door): Door => {
                    const readers = door['terminals'];
                    const entryReader = readers.find(reader => reader['dev-direction'] === 0);
                    const exitReader = readers.find(reader => reader['dev-direction'] === 1);

                    return {
                        id: door['id'],
                        name: door['loc-name'],
                        controller: door['zone-by-zone-id']['controller']['dev-name'],
                        site: door['zone-by-zone-id']['controller']['site']['site-name'],
                        zone: door['zone-by-zone-id']['zone-name'],
                        entryReader: entryReader ? entryReader['dev-fixed-addr'] : '',
                        exitReader: exitReader ? exitReader['dev-fixed-addr'] : '',
                        siteId: door['zone-by-zone-id']['controller']['site']['id'],
                    }
                });

                return resolve({ rows: zoneDoors, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDoorNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `location?include=zoneByZoneId&fields[location]=locName,zoneByZoneId&fields[zone]=zoneName${paginationString}`;
        const updatedLocationFilter = url.replace('location.locName', 'locName');
        const response = await apiConnector.fetchData(updatedLocationFilter);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiDoors: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const doors: Name[] = apiDoors.map((door) => {
                    return { 
                        label: door['loc-name'], 
                        value: door['id'],
                        optionalColumns: [{ key: 'zone', value: door["zone-by-zone-id"]["zone-name"] || '-' }],
                    };
                });

                resolve({ rows: doors, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDoorNames = async (): Promise<Name[]> => {
    try {
        const url = `location?fields[location]=locName`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, doorNames: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const doorsName: Name[] = doorNames.map((door): Name => {
                    return {
                        value: door['id'],
                        label: door['loc-name']
                    };
                });

                return resolve(doorsName);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const findBuildingOrFloorAttributesForDoor = (entity: string, attribute: string, entryReader, exitReader) => {
    return entryReader && entryReader[entity]
        ? entryReader[entity][attribute]
        : exitReader && exitReader[entity]
            ? exitReader[entity][attribute]
            : undefined;
}

const buildDoor = (door: any) => {
    const readers = door['terminals'];
    
    const entryReader = readers.find(reader => reader['dev-direction'] === 0);
    const exitReader = readers.find(reader => reader['dev-direction'] === 1);
    const zoneByInterleadingZone = door['zone-by-interleading-zone-id'];

    return {
        id: door['id'],
        name: door['loc-name'],
        nameOriginal: door['loc-name'],
        zone: door['zone-by-zone-id']['zone-name'],
        zoneId: door['zone-by-zone-id']['id'],
        controller: door['zone-by-zone-id']['controller']['device-improx']['dev-name'],
        controllerId: door['zone-by-zone-id']['controller']['device-improx']['id'],
        site: door['zone-by-zone-id']['controller']['site']['site-name'],
        siteId: door['zone-by-zone-id']['controller']['site']['id'],
        entryReader: entryReader ? entryReader['dev-name'] : '',
        entryReaderId: entryReader ? entryReader['id'] : '',
        exitReader: exitReader ? exitReader['dev-name'] : '',
        exitReaderId: exitReader ? exitReader['id'] : '',
        readers: readers?.length,
        multiTag: door['loc-multipletag'],
        zoneByInterleadingZoneId: zoneByInterleadingZone ? zoneByInterleadingZone['id'] : undefined,
        zoneByInterleadingZoneName: zoneByInterleadingZone ? zoneByInterleadingZone['zone-name'] : undefined,
        buildingId: findBuildingOrFloorAttributesForDoor('building', 'id', entryReader, exitReader),
        floorId: findBuildingOrFloorAttributesForDoor('floor', 'id', entryReader, exitReader),
        building: findBuildingOrFloorAttributesForDoor('building', 'building-name', entryReader, exitReader),
        floor: findBuildingOrFloorAttributesForDoor('floor', 'floor-name', entryReader, exitReader),
        description: door['loc-desc'] ?? '',
    };
}

export const storeDoor = async (door: Door, createDoor = true): Promise<StoreEntityResult> => {
    try {
        const terminals = [{
            type: 'terminal',
            id: door.entryReaderId,
            entryDevice: true
        },
        {
            type: 'terminal',
            id: door.exitReaderId,
            entryDevice: false
        }].filter(terminal => terminal.id);
        const devices = terminals.map((terminal) => {
            return {
                ...terminal,
                buildingId: door.buildingId,
                floorId: door.floorId,
            }
        })
        const newDoor = {
            data: {
                type: 'location',
                id: door.id,
                attributes: {
                    locMultipletag: door.multiTag ?? 0,
                    locName: door.name,
                    locDesc: door.description,
                },
                relationships: {
                    zoneByInterleadingZoneId: {
                        data: door.zoneByInterleadingZoneId
                            ? {
                                type: 'zone',
                                id: door.zoneByInterleadingZoneId
                            }
                            : undefined
                    },
                    zoneByZoneId: {
                        data: {
                            type: 'zone',
                            id: door.zoneId
                        }
                    }
                }
            }
        }

        const url = `location${createDoor ? '' : `/${newDoor.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createDoor, newDoor).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createDoor) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, async (error, apiResult) => {
                        if (error) {
                            return reject({ status: response.status, id: newDoor.data.id, success: false, message: genericError });
                        }
                        const terminalPromises = devices.map((terminal) => {
                            return storeTerminal(terminal, createDoor ? apiResult.id : newDoor.data.id, terminal.entryDevice)
                        });
                        await Promise.all(terminalPromises)
                            .then(async (terminalResponse) => {
                            }).catch(error => {
                                return new Promise((resolve, reject) => {
                                    return reject(processServerError(error));
                                })
                            })
                        return resolve({ id: apiResult.id, success: true });
                    })
                    return;

                }
                else {
                    if (response.status === NO_CONTENT) {
                        const terminalPromises = devices.map((terminal) => {
                            return storeTerminal(terminal, newDoor.data.id, terminal.entryDevice)
                        });
                        await Promise.all(terminalPromises)
                            .then(async (terminalResponse) => {
                            }).catch(error => {
                                return new Promise((resolve, reject) => {
                                    return reject(processServerError(error));
                                })
                            })
                        return resolve({ id: newDoor.data.id, success: true })
                    }

                    const content = await response.json();
                    return reject({ status: response.status, id: newDoor.data.id, success: false, message: getConstraintViolation(content.errors[0].detail) });
                }
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: newDoor.data.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const storeTerminal = async (terminal, doorId, entryDevice?: boolean) => {
    try {
        const url = `terminal/${terminal.id}`;
        const newTerminal = {
            data: {
                type: "terminal",
                id: terminal.id,
                attributes: {
                    devDirection: entryDevice !== undefined ? entryDevice ? 0 : 1 : 2
                },
                relationships: {
                    location: {
                        data: doorId
                            ? {
                                type: "location",
                                id: doorId
                            }
                            : undefined
                    },
                    building: {
                        data: terminal.buildingId ? {
                            type: "building",
                            id: terminal.buildingId
                        } : undefined
                    },
                    floor: {
                        data: terminal.floorId ? {
                            type: "floor",
                            id: terminal.floorId
                        } : undefined
                    }
                }
            }
        }
        return apiConnector.sendData(url, false, newTerminal)
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteDoor = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `location/${id}`;
        const door = {
            data: {
                type: "location",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, door)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getFilteredDoorNamesWithPagination = async (filters?: Filter[], pagination?: PaginationModel): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const partialURL = createPartialURL(pagination?.pageNumber, pagination?.maxPageSize, undefined, getFilterClause(filters));
        const url = `location?include=zoneByZoneId.controller.deviceImprox.site&fields[location]=locName${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiDoors: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const doors: Name[] = apiDoors.map((door) => {
                    return { label: door['loc-name'], value: door['id'] };
                });

                resolve({ rows: doors, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
